import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from "react";
import { usePathname } from "wouter/use-browser-location";
import { trimRunEnd } from "@shared/trimRuns";
import { splitInstancePath } from "@/lib/instancePath";

/**
 * The page notice: where success and info land, and errors whose control is gone
 * (ux-s10.md §3, contract §14 P13a).
 *
 * One rule for the whole app, changed once rather than screen by screen:
 * - a notice renders in flow between the page header and `<main>` (`NoticeRegion`),
 *   so it pushes content down and never covers a control;
 * - success and info without an action clear after `NOTICE_LIFETIME_MS`, and the
 *   timer is paused while the notice is hovered or holds focus (the provider owns
 *   the timer: `SkyMessageBar`'s own `duration` cannot pause);
 * - a notice with an action stays, and errors and warnings stay, until dismissed;
 * - every notice clears on a route change, except one posted with `forPath` equal
 *   to the page being navigated to, which survives that one change and clears on
 *   the next (create then open the new page; start an import then open it);
 * - the newest `MAX_NOTICES` render. Past that, the oldest notice that would have
 *   cleared on its own (success or info, no action) is dropped; an error, a warning
 *   or a notice with an action is never dropped, only kept out of view until a
 *   newer notice goes, because it may be the only place a reference id appears.
 *
 * The call shape is `useToast`'s (`success|info|warning|error(message, options)`)
 * so a call site migrates by rename. Two differences are deliberate: `message` is
 * a string, because the same text is what the live line announces; and there is
 * no `content`, which the library rendered INSTEAD of the message (the bug fixed
 * in `useApiErrorNotice`).
 */

export type NoticeIntent = "success" | "info" | "warning" | "error";

export interface NoticeAction {
  label: string;
  /**
   * Receives the notice id, so an action can dismiss its own notice. Resolving to
   * `false` says the action did not happen (a refused clipboard), and the button
   * then keeps its label rather than showing `doneLabel`.
   */
  onClick: (noticeId: string) => void | boolean | Promise<void | boolean>;
  /** Shown in place of `label` for 2 s after the action succeeds ("Copied"). */
  doneLabel?: string;
}

export interface NoticeOptions {
  title?: string;
  /** One action, rendered as a small button. A notice with an action stays. */
  action?: NoticeAction;
  /**
   * An ABSOLUTE path the notice is meant for. Post it just before `navigate()`:
   * the notice survives the navigation to this path and clears on the next one.
   * A wouter `~` prefix (`absolutePath()`), a query string and a hash are ignored,
   * because the provider sits above every nested router and compares full
   * pathnames.
   */
  forPath?: string;
  /**
   * A stable name for the notice. Posting again with the same key replaces the
   * earlier notice, and `dismiss(key)` or `useDismissNoticeWhen(key, …)` clears it.
   */
  key?: string;
}

export interface Notice {
  id: string;
  key: string | null;
  intent: NoticeIntent;
  title: string | null;
  message: string;
  action: NoticeAction | null;
  forPath: string | null;
  /** True once the navigation `forPath` was waiting for has happened. */
  arrived: boolean;
}

export interface NoticeApi {
  success: (message: string, options?: NoticeOptions) => string;
  info: (message: string, options?: NoticeOptions) => string;
  warning: (message: string, options?: NoticeOptions) => string;
  error: (message: string, options?: NoticeOptions) => string;
  /** Dismiss by the id a post returned, or by its `key`. Unknown ids are ignored. */
  dismiss: (idOrKey: string) => void;
}

/** What one live line is currently saying, keyed by the notice that said it. */
export interface Announcement {
  noticeId: string;
  text: string;
}

export type HoldReason = "hover" | "focus";

export interface NoticeStateValue {
  /** Every notice held, newest first; `NoticeRegion` renders `visibleNotices` of it. */
  notices: Notice[];
  announcements: { polite: Announcement | null; assertive: Announcement | null };
  hold: (noticeId: string, reason: HoldReason, held: boolean) => void;
}

export const NOTICE_LIFETIME_MS = 10_000;
export const MAX_NOTICES = 2;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface State {
  /** Newest first. */
  notices: Notice[];
  announcements: NoticeStateValue["announcements"];
  /** The normalised pathname the notices were last reconciled against. */
  path: string;
}

type Action =
  | { type: "post"; notice: Notice }
  | { type: "dismiss"; target: string }
  | { type: "route"; path: string };

/**
 * `~/runs/7?tab=events#top`, `/runs/7/` and `/i/<id>/runs/7` all name `/runs/7`. wouter's `~`
 * marks a path as absolute inside a nested router (`routes.ts` `absolutePath`), and the instance
 * prefix (round 9 T-58) is the shell's, not the screen's: a change of instance is
 * `NoticesFollowInstance`'s business (`App.tsx`).
 */
export function normalizeNoticePath(path: string): string {
  let out = path.startsWith("~") ? path.slice(1) : path;
  out = splitInstancePath(out).inner;
  out = out.split(/[?#]/, 1)[0] ?? "";
  if (!out.startsWith("/")) out = `/${out}`;
  if (out.length > 1 && out.endsWith("/")) out = trimRunEnd(out, "/") || "/";
  return out;
}

/** An announcement survives only while the notice that made it is on screen. */
function pruneAnnouncements(
  notices: Notice[],
  announcements: State["announcements"],
): State["announcements"] {
  const alive = (a: Announcement | null) =>
    a && notices.some((notice) => notice.id === a.noticeId) ? a : null;
  const polite = alive(announcements.polite);
  const assertive = alive(announcements.assertive);
  return polite === announcements.polite && assertive === announcements.assertive
    ? announcements
    : { polite, assertive };
}

/** The text the live line reads: the title, then the message, as the bar shows them. */
export function announcementText(notice: Pick<Notice, "title" | "message">): string {
  if (!notice.title) return notice.message;
  const joiner = /[.!?:]$/.test(notice.title) ? " " : ". ";
  return `${notice.title}${joiner}${notice.message}`;
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "post": {
      const { notice } = action;
      const others = notice.key
        ? state.notices.filter((existing) => existing.key !== notice.key)
        : state.notices;
      const notices = withinCap([notice, ...others]);
      const said = { noticeId: notice.id, text: announcementText(notice) };
      // Errors go to the assertive line, everything else to the polite one: one
      // element announces each notice, exactly once, when it is posted.
      const announcements =
        notice.intent === "error"
          ? { ...state.announcements, assertive: said }
          : { ...state.announcements, polite: said };
      return { ...state, notices, announcements: pruneAnnouncements(notices, announcements) };
    }
    case "dismiss": {
      const notices = state.notices.filter(
        (notice) => notice.id !== action.target && notice.key !== action.target,
      );
      if (notices.length === state.notices.length) return state;
      return {
        ...state,
        notices,
        announcements: pruneAnnouncements(notices, state.announcements),
      };
    }
    case "route": {
      if (action.path === state.path) return state;
      const notices = state.notices
        .filter((notice) => !notice.arrived && notice.forPath === action.path)
        .map((notice) => ({ ...notice, arrived: true }));
      return {
        path: action.path,
        notices,
        announcements: pruneAnnouncements(notices, state.announcements),
      };
    }
  }
}

function autoClears(notice: Notice): boolean {
  return (notice.intent === "success" || notice.intent === "info") && notice.action === null;
}

/**
 * Newest first in, newest first out. While more than `MAX_NOTICES` are held, drop
 * the oldest notice, other than the one just posted, that clears on its own. What
 * is left past the cap stays until dismissed and waits out of view (errors stay:
 * ux-s10.md §3 rule 2, contract §14).
 */
function withinCap(notices: Notice[]): Notice[] {
  const kept = [...notices];
  while (kept.length > MAX_NOTICES) {
    let oldest = -1;
    for (let index = kept.length - 1; index >= 1; index -= 1) {
      if (autoClears(kept[index]!)) {
        oldest = index;
        break;
      }
    }
    if (oldest === -1) break;
    kept.splice(oldest, 1);
  }
  return kept;
}

/** The notices on screen: the newest `MAX_NOTICES`. */
export function visibleNotices(notices: Notice[]): Notice[] {
  return notices.slice(0, MAX_NOTICES);
}

let nextNoticeId = 0;

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const NoticeApiContext = createContext<NoticeApi | null>(null);
const NoticeStateContext = createContext<NoticeStateValue | null>(null);

interface LifetimeTimer {
  handle: ReturnType<typeof setTimeout> | null;
  remaining: number;
  startedAt: number;
  holds: Set<HoldReason>;
}

export function NoticeProvider({
  children,
  lifetimeMs = NOTICE_LIFETIME_MS,
}: {
  children: ReactNode;
  /** Tests only; the product lifetime is `NOTICE_LIFETIME_MS`. */
  lifetimeMs?: number;
}) {
  // The BROWSER pathname, not wouter's `useLocation`: the provider must compare
  // full paths wherever it is mounted, and inside a nested router `useLocation`
  // answers relative to that router's base.
  const pathname = normalizeNoticePath(usePathname());
  const [state, dispatch] = useReducer(reducer, pathname, (path) => ({
    notices: [],
    announcements: { polite: null, assertive: null },
    path,
  }));

  // A route change is reconciled during render, not in an effect, so the first
  // frame of the new page never shows the old page's notices. Posting then
  // navigating in one handler is batched: the post is reduced first, then this.
  if (pathname !== state.path) {
    dispatch({ type: "route", path: pathname });
  }

  const timers = useRef(new Map<string, LifetimeTimer>());

  const start = useCallback((noticeId: string, timer: LifetimeTimer) => {
    timer.startedAt = Date.now();
    timer.handle = setTimeout(() => {
      timers.current.delete(noticeId);
      dispatch({ type: "dismiss", target: noticeId });
    }, timer.remaining);
  }, []);

  // Keep one timer per auto-clearing notice on screen, and none for a notice that
  // has gone (dismissed, replaced, dropped past the cap, or cleared by a route).
  // Only a notice that stays until dismissed ever waits out of view (`withinCap`).
  useEffect(() => {
    const live = timers.current;
    const onScreen = new Set(state.notices.map((notice) => notice.id));
    for (const [noticeId, timer] of live) {
      if (onScreen.has(noticeId)) continue;
      if (timer.handle) clearTimeout(timer.handle);
      live.delete(noticeId);
    }
    for (const notice of visibleNotices(state.notices)) {
      if (!autoClears(notice) || live.has(notice.id)) continue;
      const timer: LifetimeTimer = {
        handle: null,
        remaining: lifetimeMs,
        startedAt: 0,
        holds: new Set(),
      };
      live.set(notice.id, timer);
      start(notice.id, timer);
    }
  }, [state.notices, lifetimeMs, start]);

  useEffect(() => {
    const live = timers.current;
    return () => {
      for (const timer of live.values()) if (timer.handle) clearTimeout(timer.handle);
      live.clear();
    };
  }, []);

  const hold = useCallback(
    (noticeId: string, reason: HoldReason, held: boolean) => {
      const timer = timers.current.get(noticeId);
      if (!timer) return;
      if (held) {
        if (timer.holds.size === 0 && timer.handle) {
          clearTimeout(timer.handle);
          timer.handle = null;
          timer.remaining = Math.max(0, timer.remaining - (Date.now() - timer.startedAt));
        }
        timer.holds.add(reason);
        return;
      }
      timer.holds.delete(reason);
      if (timer.holds.size === 0 && !timer.handle) start(noticeId, timer);
    },
    [start],
  );

  const api = useMemo<NoticeApi>(() => {
    const post =
      (intent: NoticeIntent) =>
      (message: string, options: NoticeOptions = {}): string => {
        nextNoticeId += 1;
        const id = `notice-${nextNoticeId}`;
        dispatch({
          type: "post",
          notice: {
            id,
            key: options.key ?? null,
            intent,
            title: options.title ?? null,
            message,
            action: options.action ?? null,
            forPath: options.forPath ? normalizeNoticePath(options.forPath) : null,
            arrived: false,
          },
        });
        return id;
      };
    return {
      success: post("success"),
      info: post("info"),
      warning: post("warning"),
      error: post("error"),
      dismiss: (idOrKey: string) => dispatch({ type: "dismiss", target: idOrKey }),
    };
  }, []);

  const stateValue = useMemo<NoticeStateValue>(
    () => ({ notices: state.notices, announcements: state.announcements, hold }),
    [state.notices, state.announcements, hold],
  );

  return (
    <NoticeApiContext.Provider value={api}>
      <NoticeStateContext.Provider value={stateValue}>{children}</NoticeStateContext.Provider>
    </NoticeApiContext.Provider>
  );
}

/** Post and dismiss page notices. Stable across renders. */
export function useNotice(): NoticeApi {
  const api = useContext(NoticeApiContext);
  if (!api) throw new Error("useNotice must be used within a NoticeProvider");
  return api;
}

/** What `NoticeRegion` renders. Not for screens: they post through `useNotice`. */
export function useNoticeState(): NoticeStateValue {
  const value = useContext(NoticeStateContext);
  if (!value) throw new Error("useNoticeState must be used within a NoticeProvider");
  return value;
}

/**
 * Clear the notice posted with `key` once `condition` holds, e.g. an editor's
 * "saved" notice once the form is dirty again, so "saved" is never shown over
 * unsaved edits. It also clears a notice posted while the condition already held.
 */
export function useDismissNoticeWhen(key: string, condition: boolean): void {
  const { dismiss } = useNotice();
  const { notices } = useNoticeState();
  const present = notices.some((notice) => notice.key === key);
  useEffect(() => {
    if (present && condition) dismiss(key);
  }, [present, condition, key, dismiss]);
}
